/**
 * FhirInspectorDrawer.js — Consola de Auditoría e Inspección FHIR (Google Cloud Healthcare API)
 * 
 * Acceso Exclusivo: Administradores del Sistema (role === "admin" | isAdmin: true)
 * Formato: Drawer lateral deslizable (Slide-over) para inspeccionar y validar la fuente de verdad médica.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  fetchFhirBundle,
  fetchOnboardingState,
  normalizeFhirObservations,
  reconcileProfileWithFhir,
} from "@/services/fhirSyncService";
import { OBSERVATION_DEFS } from "@/data/loinc";
import {
  X, RefreshCw, Copy, Check, Database, Activity, ShieldCheck,
  Clock, Search, Layers, FileCode2, Terminal,
  UserCheck, Stethoscope, Heart, Scale, AlertCircle, CheckCircle2
} from "lucide-react";

export default function FhirInspectorDrawer({ isOpen, onClose }) {
  const { user } = useAuth();
  const {
    profile,
    isImpersonating,
    impersonatedUser,
    reloadProfile,
    updateProfileFields,
  } = useUserProfile();

  const [activeTab, setActiveTab] = useState("summary"); // "summary" | "loinc" | "raw" | "actions"
  const [categoryFilter, setCategoryFilter] = useState("all"); // "all" | "loinc" | "we360"
  const [loading, setLoading] = useState(false);
  const [syncingNow, setSyncingNow] = useState(false);
  const [targetEmail, setTargetEmail] = useState("");
  const [fhirData, setFhirData] = useState(null); // { ok, resources, raw, error }
  const [onboardingState, setOnboardingState] = useState(null);
  const [copied, setCopied] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [logs, setLogs] = useState([]);

  // Determinar el email prioritario a inspeccionar
  const defaultEmail = useMemo(() => {
    if (isImpersonating && impersonatedUser?.email) {
      return impersonatedUser.email;
    }
    return profile?.email || user?.email || "";
  }, [isImpersonating, impersonatedUser, profile?.email, user?.email]);

  useEffect(() => {
    if (defaultEmail) {
      setTargetEmail((prev) => (prev ? prev : defaultEmail));
    }
  }, [defaultEmail]);

  const addLog = useCallback((msg, type = "info") => {
    const time = new Date().toLocaleTimeString();
    setLogs((prev) => [{ time, msg, type }, ...prev.slice(0, 49)]);
  }, []);

  // Carga de datos FHIR desde Google Cloud Healthcare API
  const loadFhirForUser = useCallback(async (email) => {
    if (!email) return;
    setLoading(true);
    addLog(`Consultando Google Cloud Healthcare API para: ${email}`, "info");

    try {
      const userPhone = profile?.phone || profile?.telefono;
      const [bundleRes, stateRes] = await Promise.all([
        fetchFhirBundle(email),
        fetchOnboardingState({ phone: userPhone, email }),
      ]);

      setFhirData(bundleRes);
      if (bundleRes.ok) {
        addLog(`Bundle FHIR recuperado con éxito (${bundleRes.resources?.length || 0} recursos).`, "success");
      } else {
        addLog(`Aviso FHIR: ${bundleRes.error || "No se encontraron recursos en GCP para este usuario."}`, "warning");
      }

      if (stateRes.ok) {
        setOnboardingState(stateRes.state);
        addLog(`Estado de onboarding GCP sincronizado.`, "info");
      }
    } catch (err) {
      addLog(`Error consultando API FHIR: ${err.message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [addLog, profile?.phone, profile?.telefono]);

  // Ejecutar carga cada vez que se abre el drawer
  useEffect(() => {
    if (isOpen && targetEmail) {
      loadFhirForUser(targetEmail);
    }
  }, [isOpen, targetEmail, loadFhirForUser]);

  // Manejo de atajo Esc para cerrar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Forzar reconciliación en caliente
  const handleForceSync = async () => {
    if (!targetEmail || syncingNow) return;
    setSyncingNow(true);
    addLog(`Iniciando reconciliación forzada en caliente con GCP...`, "info");

    try {
      const userPhone = profile?.phone || profile?.telefono;
      const bundleRes = await fetchFhirBundle(targetEmail);
      const stateRes = await fetchOnboardingState({ phone: userPhone, email: targetEmail });

      setFhirData(bundleRes);
      if (stateRes.ok) setOnboardingState(stateRes.state);

      if (bundleRes.patientFound === false && (profile?.completedAt || profile?.consentimiento)) {
        addLog(`Usuario no registrado en GCP. Disparando aprovisionamiento en FHIR Store...`, "info");
        try {
          const { syncF1 } = await import("@/services/backendSync");
          const syncRes = await syncF1(profile, targetEmail);
          if (syncRes?.ok) {
            addLog(`Aprovisionamiento enviado a cola de GCP FHIR exitosamente.`, "success");
          } else {
            addLog(`Aviso al enviar a GCP: ${syncRes?.error || "Respuesta no OK"}`, "warning");
          }
        } catch (e) {
          addLog(`Error al auto-aprovisionar: ${e.message}`, "error");
        }
      }

      if (bundleRes.ok && bundleRes.resources?.length > 0) {
        const normObs = normalizeFhirObservations(bundleRes.resources);
        const reconciled = reconcileProfileWithFhir(profile || {}, normObs, stateRes.state);

        // Guardar actualización en Firestore si estamos auditando al usuario local
        if (updateProfileFields) {
          await updateProfileFields({
            ...reconciled.reconciledProfile,
            fhirReconciled: true,
            lastFhirSyncAt: new Date().toISOString(),
          });
        }
        if (reloadProfile) await reloadProfile();

        addLog(`Reconciliación completada. Perfil y Firestore actualizados exitosamente.`, "success");
      } else {
        addLog(`No se obtuvieron nuevas observaciones para reconciliar.`, "warning");
      }
    } catch (err) {
      addLog(`Fallo durante la sincronización forzada: ${err.message}`, "error");
    } finally {
      setSyncingNow(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Filtrado y normalización de observaciones
  const normalizedObservations = useMemo(() => {
    return fhirData?.resources ? normalizeFhirObservations(fhirData.resources) : {};
  }, [fhirData?.resources]);

  const obsList = useMemo(() => {
    return Object.values(normalizedObservations);
  }, [normalizedObservations]);

  // Extraer condiciones clínicas (SNOMED) desde GCP
  const conditions = useMemo(() => {
    const rawConditions = fhirData?.raw?.profile?.conditions;
    if (Array.isArray(rawConditions) && rawConditions.length > 0) {
      const seen = new Set();
      return rawConditions.filter((c) => {
        if (!c.display || seen.has(c.display)) return false;
        seen.add(c.display);
        return true;
      });
    }
    if (fhirData?.resources) {
      const seen = new Set();
      return fhirData.resources
        .filter((r) => r.resourceType === "Condition")
        .filter((c) => {
          if (!c.display || seen.has(c.display)) return false;
          seen.add(c.display);
          return true;
        });
    }
    return [];
  }, [fhirData]);

  // Identificador de código LOINC oficial
  const isLoincObs = useCallback((obs) => {
    return obs.system === "http://loinc.org" || /^\d{3,5}-\d$/.test(obs.code);
  }, []);

  // Filtrado por categoría
  const categorizedObs = useMemo(() => {
    return obsList.filter((obs) => {
      if (categoryFilter === "loinc") return isLoincObs(obs);
      if (categoryFilter === "we360") return !isLoincObs(obs);
      return true;
    });
  }, [obsList, categoryFilter, isLoincObs]);

  // Filtrado por buscador
  const filteredObs = useMemo(() => {
    return categorizedObs.filter((obs) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        obs.code?.toLowerCase().includes(q) ||
        obs.display?.toLowerCase().includes(q) ||
        String(obs.value).toLowerCase().includes(q)
      );
    });
  }, [categorizedObs, searchQuery]);

  const rawJsonString = useMemo(() => {
    if (fhirData?.raw) return JSON.stringify(fhirData.raw, null, 2);
    return JSON.stringify({ message: "No hay bundle crudo disponible", data: fhirData }, null, 2);
  }, [fhirData]);

  // Regla biomecánica Cintura-Altura
  const cintura = profile?.cintura || normalizedObservations["8280-0"]?.value;
  const altura = profile?.altura || normalizedObservations["8302-2"]?.value;
  const whtr = cintura && altura && altura > 0 ? (cintura / altura).toFixed(2) : null;
  const whtrHigh = whtr && parseFloat(whtr) > 0.5;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex justify-end animate-fade-in" data-testid="fhir-inspector-drawer">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Slide-over Panel */}
      <div className="relative w-full max-w-2xl bg-white h-full shadow-2xl flex flex-col z-10 border-l border-slate-200">
        
        {/* Header estilo Consola DevTools */}
        <div className="bg-slate-900 text-slate-100 px-6 py-4 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-teal-500/20 border border-teal-500/30 flex items-center justify-center text-teal-400">
              <Database size={18} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-sm tracking-tight text-white flex items-center gap-1.5">
                  Consola de Auditoría FHIR
                  <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-teal-500/20 text-teal-300 border border-teal-500/30">
                    HL7 R4 GCP
                  </span>
                </h3>
              </div>
              <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5">
                <UserCheck size={12} className="text-teal-400" />
                <span className="font-mono text-slate-200">{targetEmail}</span>
                {isImpersonating && (
                  <span className="text-[9px] bg-purple-500/20 text-purple-300 border border-purple-500/30 px-1.5 py-0.2 rounded font-bold">
                    Impersonando
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => loadFhirForUser(targetEmail)}
              disabled={loading}
              title="Recargar datos FHIR desde GCP"
              className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors cursor-pointer"
            >
              <RefreshCw size={15} className={loading ? "animate-spin text-teal-400" : ""} />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-slate-800 hover:bg-rose-900/40 text-slate-400 hover:text-rose-300 transition-colors cursor-pointer"
              title="Cerrar consola (Esc)"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Barra de navegación de Pestañas (Tabs) */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-6 gap-2 text-xs font-semibold overflow-x-auto">
          {[
            { id: "summary", label: "Resumen & Estado", icon: Activity },
            { id: "loinc", label: `Observaciones LOINC (${obsList.length})`, icon: Layers },
            { id: "raw", label: "Raw FHIR Bundle", icon: FileCode2 },
            { id: "actions", label: "Acciones & Diagnóstico", icon: Terminal },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-3 flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                  active
                    ? "border-teal-600 text-teal-800 font-bold bg-white"
                    : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
                }`}
              >
                <Icon size={14} className={active ? "text-teal-600" : "text-slate-400"} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Selector dinámico de usuario para el admin */}
        <div className="px-6 py-2.5 bg-slate-100/70 border-b border-slate-200/80 flex items-center gap-3">
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider shrink-0">
            Usuario Auditado:
          </label>
          <input
            type="email"
            value={targetEmail}
            onChange={(e) => setTargetEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") loadFhirForUser(targetEmail);
            }}
            placeholder="Introduce email de usuario..."
            className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-slate-300 bg-white font-mono text-slate-800 focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
          <button
            onClick={() => loadFhirForUser(targetEmail)}
            disabled={loading}
            className="px-3 py-1.5 text-xs font-bold rounded-lg bg-slate-900 text-white hover:bg-teal-700 transition-colors shrink-0 cursor-pointer"
          >
            Consultar
          </button>
        </div>

        {/* Contenido según pestaña */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5 bg-[#FCFAEE]/30">
          
          {/* TAB 1: RESUMEN & ESTADO */}
          {activeTab === "summary" && (
            <div className="space-y-4">
              
              {/* Tarjeta de Estado de Reconciliación */}
              <div className="p-4 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-black tracking-wider uppercase text-slate-400">
                    Estado en Google Cloud Healthcare API
                  </span>
                  {profile?.fhirReconciled || fhirData?.ok ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-teal-50 text-teal-700 border border-teal-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
                      Reconciliado con FHIR
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                      <Clock size={11} />
                      Sincronización Pendiente
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-1">
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="text-[10px] font-bold uppercase text-slate-400">Último Sync</p>
                    <p className="text-xs font-mono font-bold text-slate-800 mt-0.5 truncate">
                      {profile?.lastFhirSyncAt
                        ? new Date(profile.lastFhirSyncAt).toLocaleString("es-PA", { dateStyle: "short", timeStyle: "short" })
                        : "No registrado"}
                    </p>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="text-[10px] font-bold uppercase text-slate-400">Clasificación GCP</p>
                    <p className="text-xs font-bold text-slate-800 mt-0.5 capitalize truncate">
                      {conditions.some((c) => c.display?.toLowerCase().includes("hta"))
                        ? "Nivel 2 (Riesgo Moderado)"
                        : profile?.fhirRiskClassification || onboardingState?.riskClassification || onboardingState?.risk_classification || "Aparentemente sano"}
                    </p>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="text-[10px] font-bold uppercase text-slate-400">Total en Store</p>
                    <p className="text-xs font-mono font-bold text-teal-700 mt-0.5">
                      {fhirData?.resources?.length || 0} entidades
                    </p>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="text-[10px] font-bold uppercase text-slate-400">Observaciones</p>
                    <p className="text-xs font-mono font-bold text-slate-700 mt-0.5">
                      {obsList.length} métricas
                    </p>
                  </div>
                </div>
              </div>

              {/* Tarjeta de Condiciones Clínicas Activas (SNOMED en GCP) */}
              <div className="p-4 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertCircle size={16} className="text-amber-600" />
                    <h4 className="text-xs font-bold text-slate-800">
                      Condiciones Clínicas Registradas en GCP
                    </h4>
                  </div>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-bold border border-slate-200">
                    {conditions.length} activas
                  </span>
                </div>

                {conditions.length === 0 ? (
                  <p className="text-xs text-slate-400 italic py-1">
                    No se registran diagnósticos ni condiciones patológicas activas en GCP.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                    {conditions.map((cond, idx) => {
                      const isHta = cond.display?.toLowerCase().includes("hta");
                      return (
                        <div
                          key={cond.id || idx}
                          className={`p-2.5 rounded-xl border flex items-start justify-between gap-2 ${
                            isHta
                              ? "bg-rose-50/70 border-rose-200 text-rose-900"
                              : "bg-amber-50/70 border-amber-200 text-amber-900"
                          }`}
                        >
                          <div className="min-w-0">
                            <p className="text-xs font-bold truncate">{cond.display}</p>
                            <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-slate-500 font-mono">
                              <span>SNOMED: {cond.code}</span>
                              {cond.status && <span>• {cond.status}</span>}
                            </div>
                          </div>
                          <span
                            className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 ${
                              isHta
                                ? "bg-rose-200/80 text-rose-800"
                                : "bg-amber-200/80 text-amber-800"
                            }`}
                          >
                            {isHta ? "Nivel 2" : "Nivel 1"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Auditoría Biomecánica: Relación Cintura-Altura (WHtR) */}
              <div className="p-4 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Stethoscope size={16} className="text-teal-600" />
                    <h4 className="text-xs font-bold text-slate-800">Biometría Cintura / Altura (WHtR)</h4>
                  </div>
                  {whtr ? (
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded border uppercase ${
                      whtrHigh
                        ? "bg-rose-50 text-rose-700 border-rose-200"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200"
                    }`}>
                      {whtrHigh ? "Nivel 2 (Riesgo Cardiometabólico)" : "Óptimo (≤ 0.50)"}
                    </span>
                  ) : (
                    <span className="text-[10px] text-slate-400">Sin datos biométricos</span>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs pt-1">
                  <div className="p-2.5 bg-slate-50 rounded-lg">
                    <span className="text-[10px] text-slate-400 block font-bold">Cintura (LOINC 8280-0)</span>
                    <span className="font-semibold text-slate-700">{cintura ? `${cintura} cm` : "—"}</span>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded-lg">
                    <span className="text-[10px] text-slate-400 block font-bold">Altura (LOINC 8302-2)</span>
                    <span className="font-semibold text-slate-700">{altura ? `${altura} cm` : "—"}</span>
                  </div>
                  <div className="p-2.5 bg-slate-50 rounded-lg">
                    <span className="text-[10px] text-slate-400 block font-bold">Índice WHtR</span>
                    <span className={`font-mono font-bold ${whtrHigh ? "text-rose-600" : "text-emerald-700"}`}>
                      {whtr || "—"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Panel de Signos Vitales y Biomarcadores Clave */}
              <div className="p-4 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Heart size={16} className="text-rose-500" />
                    <h4 className="text-xs font-bold text-slate-800">
                      Signos Vitales y Biomarcadores Recientes (GCP)
                    </h4>
                  </div>
                  <span className="text-[10px] text-slate-400">Fuente HL7 FHIR</span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs pt-1">
                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 block font-bold">Presión Arterial</span>
                    <span className="font-mono font-bold text-slate-800">
                      {normalizedObservations["8480-6"]?.value || "—"} / {normalizedObservations["8462-4"]?.value || "—"}
                    </span>
                    <span className="text-[10px] text-slate-400 block">mmHg</span>
                  </div>

                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 block font-bold">Frecuencia Cardíaca</span>
                    <span className="font-mono font-bold text-slate-800">
                      {normalizedObservations["8867-4"]?.value || "—"}
                    </span>
                    <span className="text-[10px] text-slate-400 block">lpm</span>
                  </div>

                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 block font-bold">Frec. Respiratoria</span>
                    <span className="font-mono font-bold text-slate-800">
                      {normalizedObservations["9279-1"]?.value || "—"}
                    </span>
                    <span className="text-[10px] text-slate-400 block">rpm</span>
                  </div>

                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 block font-bold">Variabilidad HRV</span>
                    <span className="font-mono font-bold text-slate-800">
                      {normalizedObservations["80404-7"]?.value || "—"}
                    </span>
                    <span className="text-[10px] text-slate-400 block">ms</span>
                  </div>

                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 block font-bold">Peso Corporal</span>
                    <span className="font-mono font-bold text-slate-800">
                      {normalizedObservations["29463-7"]?.value || "—"}
                    </span>
                    <span className="text-[10px] text-slate-400 block">kg</span>
                  </div>

                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 block font-bold">Horas de Sueño</span>
                    <span className="font-mono font-bold text-slate-800">
                      {normalizedObservations["93832-4"]?.value || "—"}
                    </span>
                    <span className="text-[10px] text-slate-400 block">horas</span>
                  </div>

                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 block font-bold">Índice de Estrés</span>
                    <span className="font-mono font-bold text-slate-800">
                      {normalizedObservations["we360-stress-index"]?.value || "—"}
                    </span>
                    <span className="text-[10px] text-slate-400 block">pts</span>
                  </div>

                  <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <span className="text-[10px] text-slate-400 block font-bold">Tabaco / Hábitos</span>
                    <span className="font-bold text-slate-800">
                      {String(normalizedObservations["72166-2"]?.value ?? "—")}
                    </span>
                    <span className="text-[10px] text-slate-400 block">Consumo</span>
                  </div>
                </div>
              </div>

              {/* Botón de Sincronización Inmediata */}
              <div className="p-4 rounded-2xl bg-teal-50 border border-teal-200/80 flex items-center justify-between">
                <div>
                  <h4 className="text-xs font-bold text-teal-900">Forzar Reconciliación en Caliente</h4>
                  <p className="text-[11px] text-teal-700 mt-0.5">
                    Descarga el Bundle de GCP e hidrata reactivamente el perfil del usuario.
                  </p>
                </div>
                <button
                  onClick={handleForceSync}
                  disabled={syncingNow}
                  className="px-4 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl text-xs font-bold shadow-sm transition-all flex items-center gap-1.5 shrink-0 cursor-pointer"
                >
                  <RefreshCw size={13} className={syncingNow ? "animate-spin" : ""} />
                  {syncingNow ? "Sincronizando..." : "Reconciliar Ahora"}
                </button>
              </div>

            </div>
          )}

          {/* TAB 2: OBSERVACIONES LOINC & CLÍNICAS */}
          {activeTab === "loinc" && (
            <div className="space-y-3">
              {/* Filtros superiores y buscador */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Filtrar por código LOINC, nombre o valor..."
                      className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-teal-500"
                    />
                  </div>
                  <span className="text-xs text-slate-500 font-mono shrink-0">
                    {filteredObs.length} métricas
                  </span>
                </div>

                {/* Sub-filtro por categoría */}
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                  <button
                    onClick={() => setCategoryFilter("all")}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                      categoryFilter === "all"
                        ? "bg-slate-900 text-white"
                        : "bg-slate-100 hover:bg-slate-200 text-slate-600"
                    }`}
                  >
                    Todas ({obsList.length})
                  </button>
                  <button
                    onClick={() => setCategoryFilter("loinc")}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer flex items-center gap-1 ${
                      categoryFilter === "loinc"
                        ? "bg-blue-600 text-white"
                        : "bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200"
                    }`}
                  >
                    <span>LOINC Oficial</span>
                    <span className="text-[10px] opacity-80">
                      ({obsList.filter(isLoincObs).length})
                    </span>
                  </button>
                  <button
                    onClick={() => setCategoryFilter("we360")}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer flex items-center gap-1 ${
                      categoryFilter === "we360"
                        ? "bg-purple-600 text-white"
                        : "bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200"
                    }`}
                  >
                    <span>WE360 & Hábitos</span>
                    <span className="text-[10px] opacity-80">
                      ({obsList.filter((o) => !isLoincObs(o)).length})
                    </span>
                  </button>
                </div>
              </div>

              {filteredObs.length === 0 ? (
                <div className="p-8 text-center bg-white rounded-2xl border border-slate-200 text-slate-400 text-xs">
                  No se encontraron observaciones para los criterios seleccionados.
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredObs.map((obs) => {
                    const def = Object.values(OBSERVATION_DEFS).find((d) => d.code === obs.code);
                    const displayName = def?.display || obs.display || "Biomarcador Clínico";
                    const isLoinc = isLoincObs(obs);

                    // Formatear valor legible
                    let formattedVal = obs.value;
                    if (typeof obs.value === "boolean") {
                      formattedVal = obs.value ? "Sí" : "No";
                    } else if (obs.value === null || obs.value === undefined) {
                      formattedVal = "—";
                    }

                    return (
                      <div
                        key={obs.code}
                        className="p-3.5 bg-white rounded-xl border border-slate-200 shadow-sm flex items-center justify-between gap-3 hover:border-teal-300 transition-colors"
                      >
                        <div className="space-y-0.5 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span
                              className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
                                isLoinc
                                  ? "bg-blue-50 text-blue-700 border border-blue-200"
                                  : "bg-purple-50 text-purple-700 border border-purple-200"
                              }`}
                            >
                              {isLoinc ? "LOINC" : "WE360"}
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-slate-100 text-slate-700 border border-slate-200">
                              {obs.code}
                            </span>
                            <h5 className="text-xs font-bold text-slate-800 truncate">
                              {displayName}
                            </h5>
                          </div>
                          {obs.effectiveDateTime && (
                            <p className="text-[10px] text-slate-400 flex items-center gap-1 font-mono">
                              <Clock size={10} />
                              {new Date(obs.effectiveDateTime).toLocaleString("es-PA")}
                            </p>
                          )}
                        </div>

                        <div className="text-right shrink-0">
                          <span className="text-sm font-mono font-black text-teal-800">
                            {formattedVal}
                          </span>
                          {obs.unit && (
                            <span className="text-[10px] text-slate-500 ml-1 font-medium">
                              {obs.unit}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: RAW FHIR BUNDLE */}
          {activeTab === "raw" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between pb-1">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  Recurso HL7 FHIR Bundle (R4)
                </span>
                <button
                  onClick={() => copyToClipboard(rawJsonString)}
                  className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                  {copied ? "¡Copiado!" : "Copiar JSON"}
                </button>
              </div>

              <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 shadow-inner">
                <pre className="text-[11px] font-mono p-4 text-emerald-400 overflow-x-auto max-h-[58vh] leading-relaxed scrollbar-thin">
                  {rawJsonString}
                </pre>
              </div>
            </div>
          )}

          {/* TAB 4: ACCIONES & DIAGNÓSTICO */}
          {activeTab === "actions" && (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-3">
                <h4 className="text-xs font-bold text-slate-800">Pruebas de Conectividad GCP</h4>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-xl">
                    <span className="font-mono text-slate-600">Proxy GCP Query</span>
                    <span className="font-mono text-[10px] px-2 py-0.5 rounded bg-teal-50 text-teal-700 font-bold border border-teal-200">
                      /api/gcp/query
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-xl">
                    <span className="font-mono text-slate-600">Onboarding State</span>
                    <span className="font-mono text-[10px] px-2 py-0.5 rounded bg-teal-50 text-teal-700 font-bold border border-teal-200">
                      /api/gcp/onboarding-state
                    </span>
                  </div>
                </div>
              </div>

              {/* Registro de Auditoría Local (Logs) */}
              <div className="p-4 rounded-2xl bg-slate-900 text-slate-200 border border-slate-800 space-y-2 shadow-lg">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-white flex items-center gap-1.5">
                    <Terminal size={14} className="text-teal-400" />
                    Registro de Eventos y Diagnóstico
                  </h4>
                  <button
                    onClick={() => setLogs([])}
                    className="text-[10px] text-slate-400 hover:text-white cursor-pointer"
                  >
                    Limpiar
                  </button>
                </div>

                <div className="bg-slate-950 p-3 rounded-xl max-h-48 overflow-y-auto font-mono text-[10px] space-y-1 text-slate-300 scrollbar-thin">
                  {logs.length === 0 ? (
                    <p className="text-slate-600 italic">Sin eventos registrados en esta sesión.</p>
                  ) : (
                    logs.map((log, idx) => (
                      <div key={idx} className="flex items-start gap-2 leading-tight">
                        <span className="text-slate-500 shrink-0">[{log.time}]</span>
                        <span
                          className={
                            log.type === "error"
                              ? "text-rose-400 font-bold"
                              : log.type === "warning"
                              ? "text-amber-300"
                              : log.type === "success"
                              ? "text-emerald-400"
                              : "text-slate-300"
                          }
                        >
                          {log.msg}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-1.5">
            <ShieldCheck size={14} className="text-teal-600" />
            <span className="font-semibold text-slate-700">Modo Administrador Audit</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-xl bg-slate-200 hover:bg-slate-300 font-bold text-slate-700 transition-colors cursor-pointer"
          >
            Cerrar Consola
          </button>
        </div>

      </div>
    </div>
  );
}
